import { apiGet, apiGetBlob, apiPost, apiPut } from "@/lib/apiClient";

import { filterParams } from "../lib/filters";
import { redactFiles, redactLanePage, redactOrder, redactSchedule, redactSummary } from "../lib/redact";
import { toPartnerSession } from "../lib/session";
import type {
  FlowPartnerKind,
  PartnerSession,
  PartnerSessionDto,
  PortalScope,
  PrinterPortalBootstrap,
  PrinterPortalCard,
  PrinterPortalFilters,
  PrinterPortalSummary,
} from "../types";
import type { PrinterPortalLane } from "../../PartnerFulfillment/types";
import { portalSessionPath } from "./partnerPortalPaths";
import { printerPortalPaths } from "./printerPortalPaths";

/** Lanes load 20 cards at a time (product spec 12). */
export const PORTAL_LANE_PAGE_SIZE = 20;

export const partnerPortalApi = {
  /** 403 FORBIDDEN when the login is not linked to an active partner of this kind (staff included). */
  async getSession(partner: FlowPartnerKind): Promise<PartnerSession> {
    const dto = await apiGet<PartnerSessionDto>(portalSessionPath(partner));
    return toPartnerSession(partner, dto);
  },
};

/**
 * The printer portal (product spec 3 APIs; api-server printer-portal). Every
 * response passes through the redaction whitelist before it is cached.
 */
export const printerPortalApi = {
  async getBootstrap(scope: PortalScope): Promise<PrinterPortalBootstrap> {
    const raw = await apiGet<{ today?: string; printer?: { nickname?: string; icon?: string | null } | null; kpis?: unknown }>(
      printerPortalPaths(scope).bootstrap,
    );
    return {
      today: String(raw.today ?? ""),
      partner: raw.printer ? { nickname: String(raw.printer.nickname ?? ""), icon: raw.printer.icon ? String(raw.printer.icon) : null } : null,
      summary: redactSummary(raw),
    };
  },

  async getSummary(scope: PortalScope, window: string): Promise<PrinterPortalSummary> {
    return redactSummary(await apiGet<unknown>(printerPortalPaths(scope).summary, { window }));
  },

  /** Counts per lane, with the board's filters (the API applies them). */
  async getCounts(scope: PortalScope, filters: PrinterPortalFilters): Promise<Record<string, number>> {
    const raw = await apiGet<{ counts?: Record<string, number> }>(printerPortalPaths(scope).counts, filterParams(filters));
    return raw.counts ?? {};
  },

  async getLanePage(scope: PortalScope, params: { lane: string; filters: PrinterPortalFilters; cursor: string | null; limit: number }) {
    const raw = await apiGet<unknown>(printerPortalPaths(scope).board, {
      ...filterParams(params.filters),
      lane: params.lane,
      limit: params.limit,
      cursor: params.cursor ?? undefined,
    });
    return redactLanePage(raw);
  },

  /** Every live part touching the range, plus the open ones not planned yet. */
  async getSchedule(scope: PortalScope, params: { from: string; to: string }): Promise<PrinterPortalCard[]> {
    return redactSchedule(await apiGet<unknown>(printerPortalPaths(scope).schedule, { from: params.from, to: params.to }));
  },

  async getOrder(scope: PortalScope, id: string) {
    return redactOrder(await apiGet<unknown>(printerPortalPaths(scope).order(id)));
  },

  async getFiles(scope: PortalScope, id: string) {
    return redactFiles(await apiGet<unknown>(printerPortalPaths(scope).files(id)));
  },

  getFile: (scope: PortalScope, id: string, uploadId: string) => apiGetBlob(printerPortalPaths(scope).file(id, uploadId)),
  getDesignZip: (scope: PortalScope, id: string) => apiGetBlob(printerPortalPaths(scope).designZip(id)),

  /** Own login only. Sends the portal lane; the server saves the stage (a hop's Shipped is Internal Transit). */
  moveStage: (scope: PortalScope, id: string, lane: PrinterPortalLane, version: number | null) =>
    apiPost<unknown>(printerPortalPaths(scope).stage(id), { stage: lane, version }),

  /** Own login only. Marks those items' blanks lines received; answers the move result (`stage` is Blanks Received once all are in). */
  confirmBlanks: (scope: PortalScope, id: string, orderItemIds: string[]) =>
    apiPost<{ stage?: string }>(printerPortalPaths(scope).confirmBlanks(id), { orderItemIds: orderItemIds.map(Number) }),

  setWorkOn: (scope: PortalScope, id: string, workOn: string | null) =>
    apiPut<unknown>(printerPortalPaths(scope).workOn, { printerOrderId: Number(id), workOn }),

  setWorkOnBulk: (scope: PortalScope, ids: string[], workOn: string | null) =>
    apiPut<unknown>(printerPortalPaths(scope).workOnBulk, { printerOrderIds: ids.map(Number), workOn }),
};
